import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TeamTreeSidebar } from "@/components/teams/team-tree-sidebar";
import { TeamDetailPanel } from "@/pages/teams/team-detail";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePageTitle } from "@/hooks/use-page-title";
import { useTeamTree } from "@/hooks/queries/use-teams";
import { FolderTree } from "lucide-react";
import { useState } from "react";

function findFirstSelectableTeam(tree: Array<{ id: string; children?: Array<{ id: string }> }>): string | null {
  if (!tree || tree.length === 0) return null;
  const firstGroup = tree[0];
  if (!firstGroup) return null;
  if (firstGroup.children && firstGroup.children.length > 0) {
    return firstGroup.children[0]?.id ?? firstGroup.id;
  }
  return firstGroup.id;
}

export function TeamsPage() {
  const { teamId } = useParams<{ teamId: string }>();
  usePageTitle("Teams");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { data: tree, isLoading } = useTeamTree();

  // Auto-select first child team when landing on /teams with no selection
  useEffect(() => {
    if (teamId || isLoading || !tree || tree.length === 0) return;
    const firstTeamId = findFirstSelectableTeam(tree);
    if (firstTeamId) {
      navigate(`/teams/${firstTeamId}`, { replace: true });
    }
  }, [teamId, isLoading, tree, navigate]);

  return (
    <div className="pt-4">
      <div className="flex h-[calc(100vh-5rem)] gap-4">
        {/* Left panel — Team hierarchy (~20% width) */}
        <div className="hidden w-[340px] shrink-0 overflow-y-auto scrollbar-none rounded-2xl lg:block">
          <TeamTreeSidebar {...(teamId ? { currentTeamId: teamId } : {})} />
        </div>

        {/* Right panel — Team content (remaining width) */}
        <div className="min-w-0 flex-1 overflow-y-auto scrollbar-none">
          {isMobile ? (
            <div className="mb-3">
              <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderTree className="mr-2 h-4 w-4" />
                    Browse hierarchy
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="border-r p-0">
                  <SheetHeader className="border-b border-white/[0.08]">
                    <SheetTitle>Team hierarchy</SheetTitle>
                  </SheetHeader>
                  <div className="p-4">
                    <TeamTreeSidebar {...(teamId ? { currentTeamId: teamId } : {})} onNavigate={() => setIsSidebarOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          ) : null}

          {teamId ? (
            <TeamDetailPanel teamId={teamId} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

